import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AdminSidebar, PageHeader, SkeletonCard, EmptyState } from "@/components/AdminLayout";
import api from "@/utils/api";

export default function ClientManagement() {
  const [clients, setClients] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    brand_color: "#0a2240",
    contact_person: "",
    contact_email: "",
    contact_phone: "",
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const [clientsRes, usersRes, coursesRes, progressRes] = await Promise.all([
        api.get("/clients"),
        api.get("/users"),
        api.get("/courses"),
        api.get("/progress/all").catch(() => ({ data: [] }))
      ]);
      
      const users = usersRes.data || [];
      const courses = coursesRes.data || [];
      const progress = progressRes.data || [];
      
      // Enhance clients with stats and subscription data
      const enhancedClients = clientsRes.data.map(client => {
        const clientWorkers = users.filter(u => u.client_id === client.id && u.role === 'worker');
        const clientAdmins = users.filter(u => u.client_id === client.id && u.role === 'client_admin');
        const clientCourses = courses.filter(c => c.client_id === client.id);
        const clientProgress = progress.filter(p => clientWorkers.find(w => w.id === p.worker_id));
        const completedCount = clientProgress.filter(p => p.completed).length;
        const totalAssignments = clientProgress.length;
        const completionRate = totalAssignments > 0 ? Math.round((completedCount / totalAssignments) * 100) : 0;
        
        // Mock subscription data
        const plans = ['Basic', 'Standard', 'Enterprise'];
        const planIndex = Math.floor(Math.random() * 3);
        const planPrices = { Basic: 299, Standard: 599, Enterprise: 999 };
        const plan = plans[planIndex];
        const statuses = ['Active', 'Active', 'Active', 'Trial', 'Overdue'];
        const status = statuses[Math.floor(Math.random() * 5)];
        
        return {
          ...client,
          workers: clientWorkers.length,
          admins: clientAdmins,
          courses: clientCourses.length,
          completionRate,
          lastActive: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
          subscription: {
            plan,
            amount: planPrices[plan],
            status,
            startDate: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: new Date(Date.now() + Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString()
          },
          contact: {
            person: client.contact_person || 'Admin User',
            email: client.contact_email || `admin@${client.slug}.com`,
            phone: client.contact_phone || '+91 98765 43210'
          }
        };
      });
      
      setClients(enhancedClients);
    } catch (error) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async () => {
    try {
      await api.post("/clients", formData);
      toast.success("Client added successfully");
      setShowAddModal(false);
      setFormData({ name: "", slug: "", brand_color: "#0a2240", contact_person: "", contact_email: "", contact_phone: "" });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add client");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      Active: 'badge-success',
      Trial: 'badge-info',
      Overdue: 'badge-danger'
    };
    return <span className={`badge ${styles[status] || 'badge-neutral'}`}>{status}</span>;
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = !searchTerm || 
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.slug.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || client.subscription.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex">
        <AdminSidebar active="clients" role="super_admin" />
        <main className="admin-content">
          <div className="mb-6">
            <div className="h-8 bg-slate-200 rounded w-48 mb-2 animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex">
      <AdminSidebar active="clients" role="super_admin" />
      <main className="admin-content">
        <PageHeader title="Client Management" subtitle={`${clients.length} organizations`}>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-[#0a2240] hover:bg-[#0f325c] text-white"
            data-testid="add-client-button"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Client
          </Button>
        </PageHeader>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Input
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-10"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-10 px-4 border border-slate-200 rounded-lg bg-white min-w-[160px]"
          >
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Trial">Trial</option>
            <option value="Overdue">Overdue</option>
          </select>
        </div>

        {/* Clients Grid */}
        {filteredClients.length === 0 ? (
          <EmptyState
            title="No Clients Found"
            description={searchTerm || filterStatus ? "Try adjusting your filters" : "Add your first client to get started"}
            action={
              <Button onClick={() => setShowAddModal(true)} className="bg-[#0a2240] hover:bg-[#0f325c] text-white">
                Add Client
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredClients.map((client) => (
              <div
                key={client.id}
                className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-lg hover:border-slate-300 transition-all cursor-pointer"
                onClick={() => setSelectedClient(client)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                      style={{ backgroundColor: client.brand_color }}
                    >
                      {client.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{client.name}</h3>
                      <p className="text-xs text-slate-500">{client.slug}</p>
                    </div>
                  </div>
                  {getStatusBadge(client.subscription.status)}
                </div>
                
                {/* Subscription Info */}
                <div className="bg-slate-50 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600">Plan</span>
                    <span className="font-semibold text-slate-900">{client.subscription.plan}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Monthly</span>
                    <span className="font-semibold text-slate-900">${client.subscription.amount}</span>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 text-center mb-4">
                  <div className="bg-blue-50 rounded-lg py-2">
                    <p className="text-lg font-bold text-blue-700">{client.workers}</p>
                    <p className="text-xs text-blue-600">Workers</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg py-2">
                    <p className="text-lg font-bold text-purple-700">{client.courses}</p>
                    <p className="text-xs text-purple-600">Courses</p>
                  </div>
                  <div className="bg-teal-50 rounded-lg py-2">
                    <p className="text-lg font-bold text-teal-700">{client.completionRate}%</p>
                    <p className="text-xs text-teal-600">Complete</p>
                  </div>
                </div>

                {/* Renewal Date */}
                <div className="flex items-center justify-between text-sm pt-3 border-t border-slate-100">
                  <span className="text-slate-500">Renews</span>
                  <span className="font-medium text-slate-700">
                    {new Date(client.subscription.endDate).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Client Modal */}
        {showAddModal && (
          <div className="modal-overlay">
            <div className="modal-content p-6 modal-responsive">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Add New Client</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Client Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Tata Motors"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="slug">Slug (URL identifier) *</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="tata-motors"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_person">Contact Person</Label>
                    <Input
                      id="contact_person"
                      value={formData.contact_person}
                      onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                      placeholder="John Doe"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone">Phone</Label>
                    <Input
                      id="contact_phone"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      placeholder="+91 98765 43210"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="contact_email">Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="admin@company.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="color">Brand Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="color"
                      type="color"
                      value={formData.brand_color}
                      onChange={(e) => setFormData({ ...formData, brand_color: e.target.value })}
                      className="w-14 h-10 p-1"
                    />
                    <Input
                      value={formData.brand_color}
                      onChange={(e) => setFormData({ ...formData, brand_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => setShowAddModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddClient}
                  className="flex-1 bg-[#0a2240] hover:bg-[#0f325c] text-white"
                >
                  Add Client
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Client Detail Modal */}
        {selectedClient && (
          <div className="modal-overlay" onClick={() => setSelectedClient(null)}>
            <div className="modal-content p-0 max-w-2xl modal-responsive" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div 
                className="p-6 text-white"
                style={{ backgroundColor: selectedClient.brand_color }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center text-2xl font-bold">
                      {selectedClient.name.charAt(0)}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">{selectedClient.name}</h2>
                      <p className="text-white/80">{selectedClient.slug}</p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedClient(null)} className="p-2 hover:bg-white/20 rounded-lg">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Contact Info */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Contact Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Contact Person</p>
                      <p className="font-medium text-slate-900">{selectedClient.contact.person}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Email</p>
                      <p className="font-medium text-slate-900 text-sm">{selectedClient.contact.email}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Phone</p>
                      <p className="font-medium text-slate-900">{selectedClient.contact.phone}</p>
                    </div>
                  </div>
                </div>

                {/* Subscription Details */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Subscription Details</h3>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Plan</p>
                        <p className="font-bold text-slate-900">{selectedClient.subscription.plan}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Amount</p>
                        <p className="font-bold text-slate-900">${selectedClient.subscription.amount}/mo</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Status</p>
                        {getStatusBadge(selectedClient.subscription.status)}
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Renewal Date</p>
                        <p className="font-medium text-slate-900">
                          {new Date(selectedClient.subscription.endDate).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Start Date</p>
                        <p className="text-sm text-slate-700">
                          {new Date(selectedClient.subscription.startDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">End Date</p>
                        <p className="text-sm text-slate-700">
                          {new Date(selectedClient.subscription.endDate).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Usage Stats */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Usage Statistics</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-blue-700">{selectedClient.workers}</p>
                      <p className="text-sm text-blue-600">Workers</p>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-purple-700">{selectedClient.courses}</p>
                      <p className="text-sm text-purple-600">Courses</p>
                    </div>
                    <div className="bg-teal-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-teal-700">{selectedClient.completionRate}%</p>
                      <p className="text-sm text-teal-600">Completion</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-amber-700">{selectedClient.admins.length}</p>
                      <p className="text-sm text-amber-600">Admins</p>
                    </div>
                  </div>
                </div>

                {/* Admins List */}
                {selectedClient.admins.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3">Client Administrators</h3>
                    <div className="space-y-2">
                      {selectedClient.admins.map((admin, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{admin.full_name || admin.email}</p>
                            <p className="text-sm text-slate-500">{admin.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Last Active */}
                <div className="pt-4 border-t border-slate-200 flex items-center justify-between text-sm">
                  <span className="text-slate-500">Last Active</span>
                  <span className="font-medium text-slate-700">
                    {new Date(selectedClient.lastActive).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
